import React from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const STATUS_CONTENT = {
  'not-enrolled': {
    icon: '🔒',
    title: '수강 등록이 필요합니다',
    desc: '이 콘텐츠는 등록된 수강생만 이용할 수 있습니다.\n담당자에게 수강 등록을 요청해 주세요.',
    color: '#1B4A8C',
    bg: '#EBF1FA',
  },
  expired: {
    icon: '⏰',
    title: '수강 기간이 만료되었습니다',
    desc: '수강 가능 기간이 종료되었습니다.\n기간 연장이 필요하시면 담당자에게 문의해 주세요.',
    color: '#D4730A',
    bg: '#FDF3E7',
  },
  inactive: {
    icon: '⛔',
    title: '수강이 정지된 계정입니다',
    desc: '계정이 비활성화되었습니다.\n자세한 내용은 담당자에게 문의해 주세요.',
    color: '#B91C1C',
    bg: '#FEF2F2',
  },
  denied: {
    icon: '🚫',
    title: '접근이 제한되었습니다',
    desc: '현재 이 콘텐츠에 접근할 수 없습니다.\n필요한 경우 담당자에게 문의해 주세요.',
    color: '#5C5C72',
    bg: '#F7F6F4',
  },
};

export default function AccessDenied() {
  const { user, isAdmin, studentStatus, studentDoc, signOut } = useAuth();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  const reason = params.get('reason');

  // 쿼리스트링 reason 우선, 없으면 studentStatus 기반 fallback
  const resolvedReason =
    reason ||
    (studentStatus === 'expired'
      ? 'expired'
      : studentStatus === 'inactive'
      ? 'inactive'
      : studentStatus === 'not-enrolled'
      ? 'not-enrolled'
      : 'denied');

  // 관리자인데 잘못 진입한 경우 홈으로 이동
  if (isAdmin) {
    window.location.replace('/');
    return null;
  }

  const content = STATUS_CONTENT[resolvedReason] || STATUS_CONTENT.denied;

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate?.() ?? new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ ...s.iconWrap, background: content.bg }}>
          <span style={{ fontSize: '28px' }}>{content.icon}</span>
        </div>

        <div style={s.title}>{content.title}</div>

        <div style={s.desc}>
          {content.desc.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < content.desc.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
        </div>

        {resolvedReason === 'expired' && studentDoc?.expiresAt && (
          <div style={s.infoBox}>
            <span style={s.infoLabel}>만료일</span>
            <span style={s.infoValue}>{formatDate(studentDoc.expiresAt)}</span>
          </div>
        )}

        {user && (
          <div style={s.accountBox}>
            <div style={s.accountLabel}>로그인 계정</div>
            <div style={s.accountEmail}>{user.email}</div>
          </div>
        )}

        <div style={s.btnRow}>
          <button style={s.btnSecondary} onClick={signOut}>
            다른 계정으로 로그인
          </button>
        </div>

        <div style={s.contact}>문의가 있으시면 담당자에게 연락해 주세요</div>
      </div>
    </div>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F7F6F4',
    padding: '24px 16px',
  },
  card: {
    width: '100%',
    maxWidth: '400px',
    background: '#FFFFFF',
    border: '0.5px solid #E2E0DB',
    borderRadius: '14px',
    padding: '40px 36px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  iconWrap: {
    width: '64px',
    height: '64px',
    borderRadius: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#1A1A2E',
    marginBottom: '10px',
    textAlign: 'center',
  },
  desc: {
    fontSize: '14px',
    color: '#5C5C72',
    lineHeight: '1.65',
    textAlign: 'center',
    marginBottom: '20px',
  },
  infoBox: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    background: '#FDF3E7',
    border: '0.5px solid #F5D5A8',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  infoLabel: {
    fontSize: '13px',
    color: '#854F0B',
  },
  infoValue: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#854F0B',
  },
  accountBox: {
    width: '100%',
    padding: '10px 14px',
    background: '#F7F6F4',
    borderRadius: '8px',
    marginBottom: '20px',
  },
  accountLabel: {
    fontSize: '11px',
    color: '#9898AA',
    marginBottom: '2px',
  },
  accountEmail: {
    fontSize: '13px',
    color: '#1A1A2E',
    fontWeight: '500',
  },
  btnRow: {
    width: '100%',
    marginBottom: '16px',
  },
  btnSecondary: {
    width: '100%',
    padding: '11px 20px',
    background: '#FFFFFF',
    border: '0.5px solid #E2E0DB',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#1A1A2E',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  contact: {
    fontSize: '12px',
    color: '#9898AA',
    textAlign: 'center',
  },
};